/**
 * TrustPage (`/trust`) — V3 redesign.
 *
 * Public page (no auth required). Cream `--canvas` background.
 *
 * Sections:
 *   1. Hero — title + subtitle + status badge.
 *   2. Four commitment cards (encryption, no training, no sharing, deletion).
 *   3. Certifications strip (SOC 2, ISO 27001, SAMA, GDPR).
 *   4. FAQ accordion (4 entries).
 *   5. Contact footer.
 */
import React, { useState } from 'react';
import { Lock, ShieldOff, Users, Trash2, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BayenahLogo } from '@/components/brand/BayenahLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import { TERMS } from '@/lib/terminology';
import { cn } from '@/lib/utils';

const COMMITMENTS = [
  {
    id: 'encryption',
    icon: Lock,
    ar_title: 'تشفير كامل',
    en_title: 'End-to-end encryption',
    ar_desc:  'كل بياناتك مشفرة أثناء النقل عبر TLS 1.3 وعند التخزين بـ AES-256.',
    en_desc:  'All your data is encrypted in transit (TLS 1.3) and at rest (AES-256).',
  },
  {
    id: 'no-training',
    icon: ShieldOff,
    ar_title: 'لا تدريب على بياناتك',
    en_title: 'We never train on your data',
    ar_desc:  'نصوص عقودك لا تُستخدم أبداً لتدريب نماذج الذكاء الاصطناعي.',
    en_desc:  'Your contract text is never used to train any AI model.',
  },
  {
    id: 'no-sharing',
    icon: Users,
    ar_title: 'لا مشاركة مع أطراف ثالثة',
    en_title: 'No third-party sharing',
    ar_desc:  'مستنداتك لا تُشارك مع جهات خارجية، ولا تُباع، ولا تُؤجَّر.',
    en_desc:  'Your documents are never shared, sold, or rented to third parties.',
  },
  {
    id: 'deletion',
    icon: Trash2,
    ar_title: 'حذف فوري ودائم',
    en_title: 'Instant, permanent deletion',
    ar_desc:  'تستطيع حذف بياناتك فوراً، ويُنفَّذ الحذف نهائياً خلال ٣٠ يوماً.',
    en_desc:  'You can delete your data at any time. Permanent deletion within 30 days.',
  },
];

const CERTS = [
  { id: 'soc2',  name: 'SOC 2 Type II', ar_status: 'قريباً',         en_status: 'In progress' },
  { id: 'iso',   name: 'ISO 27001',     ar_status: 'قريباً',         en_status: 'In progress' },
  { id: 'sama',  name: 'SAMA',          ar_status: 'جارٍ التقييم',   en_status: 'Under review' },
  { id: 'gdpr',  name: 'GDPR',          ar_status: 'مفعّل',          en_status: 'Active' },
];

const FAQ = [
  {
    id: 'q1',
    ar_q: 'هل تحتفظ بيّنة بنسخة من العقود التي أرفعها؟',
    en_q: 'Does Bayenah retain copies of the contracts I upload?',
    ar_a: 'فقط في خزانتك القانونية الخاصة. يمكنك حذف أي مستند في أي وقت من واجهة الخزانة.',
    en_a: 'Only in your private Vault. You can delete any document at any time from the Vault UI.',
  },
  {
    id: 'q2',
    ar_q: 'أين تُخزَّن بياناتي جغرافياً؟',
    en_q: 'Where is my data stored geographically?',
    ar_a: 'في مراكز بيانات داخل المملكة العربية السعودية، التزاماً بنظام حماية البيانات الشخصية (PDPL).',
    en_a: 'Inside Saudi-Arabia data centres, in line with the Personal Data Protection Law (PDPL).',
  },
  {
    id: 'q3',
    ar_q: 'من يستطيع رؤية محتوى محادثاتي؟',
    en_q: 'Who can see the contents of my chats?',
    ar_a: 'لا أحد — لا فريق بيّنة، ولا أعضاء فريقك، إلا إذا شاركت محادثة صراحةً عبر رابط مشاركة.',
    en_a: 'No one — neither the Bayenah team nor your team members — unless you explicitly share a conversation.',
  },
  {
    id: 'q4',
    ar_q: 'كيف أُلغي حسابي؟',
    en_q: 'How do I cancel my account?',
    ar_a: 'من الإعدادات → الخصوصية والبيانات → حذف الحساب. يكتمل الحذف نهائياً خلال ٣٠ يوماً.',
    en_a: 'Settings → Privacy & Data → Delete account. Permanent deletion completes within 30 days.',
  },
];

export default function TrustPage() {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [open, setOpen] = useState(null);

  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      data-theme="harvey"
      className="min-h-screen w-full"
      style={{ background: 'var(--canvas)' }}
      data-testid="trust-page"
    >
      {/* Slim public nav */}
      <header
        className={cn('flex items-center justify-between px-6 md:px-12 h-[56px]', isArabic && 'flex-row-reverse')}
        style={{ borderBottom: 'var(--border-default)' }}
      >
        <Link to="/" data-testid="trust-back-home" className="inline-flex items-center">
          <BayenahLogo height={26} variant="dark" />
        </Link>
        <nav className={cn('flex items-center gap-4', isArabic && 'flex-row-reverse')}>
          <Link to="/" className="text-[13px] font-arabic" style={{ color: 'var(--text-secondary)' }}>
            {isArabic ? 'العودة' : 'Home'}
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto text-center px-6 py-20" data-testid="trust-hero">
        <h1
          className="font-arabic mb-3"
          style={{
            fontSize: '32px',
            color: 'var(--text-primary)',
            fontFamily: isArabic ? 'Cairo, sans-serif' : 'Cormorant Garamond, Georgia, serif',
            fontWeight: 600,
          }}
        >
          {isArabic ? 'أمانك أولويتنا' : 'Your security is our priority'}
        </h1>
        <p className="font-arabic" style={{ fontSize: '15px', color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto' }}>
          {isArabic
            ? 'بيّنة مبني على مبادئ الخصوصية والأمان من اليوم الأول — ليس كإضافة لاحقة.'
            : 'Bayenah is built on privacy and security from day one — not as an afterthought.'}
        </p>
        <div
          className="inline-flex items-center gap-2 mt-5 font-arabic"
          style={{
            background: 'var(--surface)',
            border: 'var(--border-default)',
            borderRadius: 'var(--radius-pill)',
            padding: '5px 12px',
            fontSize: '12px',
            color: 'var(--text-primary)',
          }}
          data-testid="trust-status-pill"
        >
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />
          {isArabic ? 'كل الأنظمة تعمل بشكل طبيعي' : 'All systems operational'}
        </div>
      </section>

      {/* 4 commitment cards */}
      <section className="max-w-5xl mx-auto px-6 pb-16" data-testid="trust-commitments">
        <div className="grid sm:grid-cols-2 gap-4">
          {COMMITMENTS.map((c) => (
            <article
              key={c.id}
              className="rounded-lg p-5"
              style={{ background: 'var(--surface)', border: 'var(--border-default)' }}
              data-testid={`trust-card-${c.id}`}
            >
              <c.icon className="w-6 h-6" strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
              <h3
                className="font-arabic mt-3"
                style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-primary)' }}
              >
                {isArabic ? c.ar_title : c.en_title}
              </h3>
              <p
                className="font-arabic mt-1.5 leading-relaxed"
                style={{ fontSize: '13px', color: 'var(--text-secondary)' }}
              >
                {isArabic ? c.ar_desc : c.en_desc}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-5xl mx-auto px-6 pb-16" data-testid="trust-certs">
        <h2
          className="font-arabic mb-4 text-center"
          style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-primary)' }}
        >
          {isArabic ? 'الشهادات والامتثال' : 'Certifications & compliance'}
        </h2>
        <div className="flex flex-wrap justify-center gap-3">
          {CERTS.map((c) => (
            <div
              key={c.id}
              className="inline-flex items-center gap-2 font-arabic"
              style={{
                background: 'var(--surface)',
                border: 'var(--border-default)',
                borderRadius: 'var(--radius-pill)',
                padding: '6px 14px',
                fontSize: '12.5px',
                color: 'var(--text-primary)',
              }}
              data-testid={`trust-cert-${c.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
              <span style={{ fontWeight: 500 }}>{c.name}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                · {isArabic ? c.ar_status : c.en_status}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-20" data-testid="trust-faq">
        <h2
          className="font-arabic mb-4 text-center"
          style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-primary)' }}
        >
          {isArabic ? 'الأسئلة الشائعة' : 'Frequently asked'}
        </h2>
        <div className="space-y-2">
          {FAQ.map((f) => {
            const isOpen = open === f.id;
            return (
              <div
                key={f.id}
                className="rounded-lg overflow-hidden"
                style={{ background: 'var(--surface)', border: 'var(--border-default)' }}
                data-testid={`trust-faq-${f.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : f.id)}
                  className={cn('w-full text-start flex items-center justify-between px-4 py-3 font-arabic', isArabic && 'flex-row-reverse text-right')}
                  style={{ fontSize: '14px', color: 'var(--text-primary)' }}
                  data-testid={`trust-faq-${f.id}-toggle`}
                  aria-expanded={isOpen}
                >
                  <span>{isArabic ? f.ar_q : f.en_q}</span>
                  <ChevronDown
                    className="w-4 h-4 transition-transform"
                    strokeWidth={1.7}
                    style={{
                      color: 'var(--text-secondary)',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                    }}
                  />
                </button>
                {isOpen && (
                  <div
                    className={cn('px-4 pb-3 font-arabic leading-relaxed', isArabic && 'text-right')}
                    style={{ fontSize: '13px', color: 'var(--text-secondary)', borderTop: 'var(--border-default)' }}
                  >
                    <div className="pt-3">{isArabic ? f.ar_a : f.en_a}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer contact */}
      <footer
        className={cn('text-center py-8 px-6 font-arabic', isArabic && 'flex-row-reverse')}
        style={{ borderTop: 'var(--border-default)', fontSize: '12.5px', color: 'var(--text-secondary)' }}
        data-testid="trust-footer"
      >
        {isArabic ? 'لأي استفسار أمني، تواصل معنا على' : 'For any security inquiry, reach us at'}{' '}
        <a href="mailto:security@bayenah.com" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
          security@bayenah.com
        </a>
      </footer>
    </div>
  );
}
